import {
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    Download as DownloadIcon,
    History as HistoryIcon,
    Person as PersonIcon,
    RestoreFromTrash as RestoreIcon,
    SwapHoriz as StageTransitionIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import JSZip from 'jszip';
import { useEffect, useMemo, useState } from 'react';
import idmlServiceInstance from '../../services/IdmlService';
import { getBookLanguageChipColor, getBookLanguageStageLabel } from '../../utils/processStageUtils';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const SAVE_TYPES = {
    INITIAL_CREATION: { label: 'AI Generated', icon: AutoAwesomeIcon, color: '#7c4dff', bgColor: 'rgba(124, 77, 255, 0.08)' },
    AUTO: { label: 'Auto Save', icon: AutoAwesomeIcon, color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.08)' },
    MANUAL: { label: 'Manual Save', icon: PersonIcon, color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.08)' },
    IMPORT: { label: 'Imported', icon: UploadIcon, color: '#9c27b0', bgColor: 'rgba(156, 39, 176, 0.08)' },
    RESTORE: { label: 'Restored', icon: RestoreIcon, color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.08)' },
    STAGE_TRANSITION: { label: 'Stage Transition', icon: StageTransitionIcon, color: '#607d8b', bgColor: 'rgba(96, 125, 139, 0.08)' },
};

const formatTime = (timestamp) => {
    if (!timestamp) return { relative: 'Unknown', full: 'Unknown date' };
    try {
        const date = new Date(timestamp);
        return {
            relative: formatDistanceToNow(date, { addSuffix: true }),
            full: date.toLocaleString(),
        };
    } catch (e) {
        return { relative: 'Unknown', full: 'Unknown date' };
    }
};

const getSaveTypeIcon = (saveType) => {
    const type = SAVE_TYPES[saveType] || { icon: HistoryIcon, color: '#757575', label: saveType || 'Unknown' };
    const IconComponent = type.icon;
    return (
        <Tooltip title={type.label}>
            <IconComponent fontSize="small" sx={{ color: type.color, verticalAlign: 'middle' }} />
        </Tooltip>
    );
};

const BookFileDetailsDialog = ({ open, onClose, book, onUpdated }) => {
    const theme = useTheme();
    useTrackUsage('BookFileDetailsDialog', 'dialog', 'folder-browser', open);

    const [bookState, setBookState] = useState(book);
    const [activeLanguage, setActiveLanguage] = useState('');
    const [historyByLanguage, setHistoryByLanguage] = useState({});
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [restoringVersion, setRestoringVersion] = useState(null);
    const [confirmingRestore, setConfirmingRestore] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreSuccess, setRestoreSuccess] = useState(null);
    const [restoreError, setRestoreError] = useState(null);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [downloadError, setDownloadError] = useState(null);

    const languages = useMemo(() => Object.keys(bookState?.targetLanguageStatus || {}), [bookState]);
    const activeLanguageStatus = activeLanguage ? bookState?.targetLanguageStatus?.[activeLanguage] : null;
    const activeHistory = activeLanguage ? historyByLanguage[activeLanguage] : null;
    const hasActiveHistory = Boolean(activeHistory?.versions?.length);

    useEffect(() => {
        if (!open) {
            return;
        }
        setBookState(book || null);
        setHistoryByLanguage({});
        const languageList = Object.keys(book?.targetLanguageStatus || {});
        setActiveLanguage((current) => current || languageList[0] || '');
    }, [open, book]);

    useEffect(() => {
        if (!open || !bookState?.id || !activeLanguage || activeLanguage in historyByLanguage) {
            return;
        }

        const fetchHistory = async () => {
            try {
                setLoadingHistory(true);
                const history = await idmlServiceInstance.getHistory(bookState.id, activeLanguage);
                setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: history }));
            } catch (error) {
                console.error('Failed to load book history', error);
                setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: null }));
            } finally {
                setLoadingHistory(false);
            }
        };

        fetchHistory();
    }, [open, bookState, activeLanguage, historyByLanguage]);

    const refreshBook = async () => {
        if (!bookState?.id) {
            return;
        }
        const latest = await idmlServiceInstance.getBook(bookState.id);
        setBookState(latest.book || latest);
        onUpdated?.(latest.book || latest);
    };

    const handleRestore = (version) => {
        setRestoringVersion(version);
        setConfirmingRestore(true);
        setRestoreError(null);
    };

    const confirmRestore = async () => {
        if (!restoringVersion || !bookState?.id || !activeLanguage) return;
        setConfirmingRestore(false);
        setRestoring(true);
        setRestoreError(null);
        try {
            await idmlServiceInstance.restoreVersion(bookState.id, activeLanguage, restoringVersion.versionNumber);
            setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: null }));
            await refreshBook();
            setRestoreSuccess({ version: restoringVersion.versionNumber });
            setTimeout(() => setRestoreSuccess(null), 5000);
        } catch (error) {
            console.error('Failed to restore book version', error);
            setRestoreError(`Failed to restore: ${error.response?.data?.message || error.message}`);
        } finally {
            setRestoring(false);
            setRestoringVersion(null);
        }
    };

    const cancelRestore = () => {
        setConfirmingRestore(false);
        setRestoringVersion(null);
    };

    const handleDownload = async (key, blobUrl, fileName) => {
        if (!blobUrl) return;
        setDownloadingKey(key);
        setDownloadError(null);
        try {
            const response = await fetch(blobUrl);
            if (!response.ok) throw new Error('Failed to download file');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            }, 100);
        } catch (error) {
            console.error('Failed to download file', error);
            setDownloadError('Failed to download the file');
        } finally {
            setDownloadingKey(null);
        }
    };

    const handleDownloadFolder = async (key, files, folderName) => {
        if (!files?.length) return;
        setDownloadingKey(key);
        setDownloadError(null);
        try {
            const zip = new JSZip();
            for (const file of files) {
                const response = await fetch(file.blobUrl);
                if (!response.ok) throw new Error('Failed to download file');
                const blob = await response.blob();
                zip.file(file.name, blob);
            }
            const zipContent = await zip.generateAsync({ type: 'blob' });
            const objectUrl = URL.createObjectURL(zipContent);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `${folderName}.zip`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            }, 100);
        } catch (error) {
            console.error('Failed to download folder', error);
            setDownloadError('Failed to download the folder');
        } finally {
            setDownloadingKey(null);
        }
    };

    const blobExtension = (blobUrl, fallback) => {
        const withoutQuery = (blobUrl || '').split('?')[0];
        const match = withoutQuery.match(/\.([a-zA-Z0-9]+)$/);
        return match ? match[1] : fallback;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            scroll="paper"
            sx={{ '& .MuiPaper-root': { borderRadius: '8px' } }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            File Details
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
                            {bookState?.name || 'Book'} – {bookState?.id || 'N/A'}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${languages.length} language${languages.length !== 1 ? 's' : ''}`}
                        sx={{ color: 'secondary.main' }}
                    />
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                {!bookState ? (
                    <Typography variant="body2" color="text.secondary">No file details available.</Typography>
                ) : (
                    <>
                        {/* Book-level details */}
                        <Card
                            variant="outlined"
                            sx={{ mb: 2, borderRadius: '8px', backgroundColor: 'rgba(46, 125, 50, 0.03)' }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                    Book Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Source Language</Typography>
                                        <Typography variant="body2">{bookState.sourceLanguage || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Process Stage</Typography>
                                        <Typography variant="body2">{bookState.processStage || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                                        <Typography variant="body2">{bookState.createdBy || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                                        <Typography variant="body2">{formatTime(bookState.createdAt).full}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Updated By</Typography>
                                        <Typography variant="body2">{bookState.updatedBy || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                                        <Typography variant="body2">{formatTime(bookState.updatedAt).full}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* File links */}
                        <Card variant="outlined" sx={{ mb: 2, borderRadius: '8px' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                    Source File Links
                                </Typography>
                                {downloadError && (
                                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDownloadError(null)}>
                                        {downloadError}
                                    </Alert>
                                )}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">IDML File</Typography>
                                        {bookState.originalIdmlBlobUrl ? (
                                            <Button
                                                size="small"
                                                startIcon={downloadingKey === 'idml' ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                                disabled={downloadingKey === 'idml'}
                                                onClick={() => handleDownload(
                                                    'idml',
                                                    bookState.originalIdmlBlobUrl,
                                                    `${bookState.name || 'book'}.${blobExtension(bookState.originalIdmlBlobUrl, 'idml')}`
                                                )}
                                                sx={{ textTransform: 'none', mt: 0.5 }}
                                            >
                                                Download original .idml
                                            </Button>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Documents (Document Fonts)</Typography>
                                        {bookState.uploadedFonts?.length ? (
                                            <Button
                                                size="small"
                                                startIcon={downloadingKey === 'fonts-folder' ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                                disabled={downloadingKey === 'fonts-folder'}
                                                onClick={() => handleDownloadFolder(
                                                    'fonts-folder',
                                                    bookState.uploadedFonts.map((font, index) => ({
                                                        name: `${font.family || `font-${index}`}.${blobExtension(font.blobUrl, 'ttf')}`,
                                                        blobUrl: font.blobUrl,
                                                    })),
                                                    'Document Fonts'
                                                )}
                                                sx={{ textTransform: 'none', mt: 0.5 }}
                                            >
                                                Download folder ({bookState.uploadedFonts.length})
                                            </Button>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No fonts uploaded</Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Links (placed images)</Typography>
                                        {bookState.uploadedImages?.length ? (
                                            <Button
                                                size="small"
                                                startIcon={downloadingKey === 'links-folder' ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                                disabled={downloadingKey === 'links-folder'}
                                                onClick={() => handleDownloadFolder(
                                                    'links-folder',
                                                    bookState.uploadedImages.map((image, index) => ({
                                                        name: image.filename || `image-${index}`,
                                                        blobUrl: image.blobUrl,
                                                    })),
                                                    'Links'
                                                )}
                                                sx={{ textTransform: 'none', mt: 0.5 }}
                                            >
                                                Download folder ({bookState.uploadedImages.length})
                                            </Button>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No links uploaded</Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Per-language history */}
                        <Card variant="outlined" sx={{ borderRadius: '8px' }}>
                            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Per-Language History
                                </Typography>
                            </Box>
                            <CardContent sx={{ p: 2 }}>
                                {languages.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No target languages added yet.</Typography>
                                ) : (
                                    <>
                                        <Paper variant="outlined" sx={{ borderRadius: 1, mb: 2 }}>
                                            <Tabs
                                                value={Math.max(languages.indexOf(activeLanguage), 0)}
                                                onChange={(_, index) => setActiveLanguage(languages[index] || '')}
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                sx={{
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    '& .MuiTab-root': { minHeight: '44px', fontWeight: 400, textTransform: 'none' },
                                                    '& .Mui-selected': { fontWeight: 600, color: 'success.main', backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                                                }}
                                            >
                                                {languages.map((lang) => (
                                                    <Tab key={lang} label={lang} />
                                                ))}
                                            </Tabs>
                                        </Paper>

                                        {activeLanguage ? (
                                            <>
                                                {/* Language metadata row */}
                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="text.secondary">Process Stage</Typography>
                                                        <Chip
                                                            label={getBookLanguageStageLabel(activeLanguageStatus?.processStage)}
                                                            size="small"
                                                            sx={{
                                                                mt: 0.5,
                                                                backgroundColor: `${getBookLanguageChipColor(activeLanguageStatus?.processStage, theme.palette.mode)}20`,
                                                                color: getBookLanguageChipColor(activeLanguageStatus?.processStage, theme.palette.mode),
                                                                border: '1px solid',
                                                                borderColor: getBookLanguageChipColor(activeLanguageStatus?.processStage, theme.palette.mode),
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="text.secondary">Exported At</Typography>
                                                        <Typography variant="body2">
                                                            {activeLanguageStatus?.exportedAt ? formatTime(activeLanguageStatus.exportedAt).full : 'Not exported'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="text.secondary">Active Editor</Typography>
                                                        <Typography variant="body2">{activeLanguageStatus?.activeEditor || 'None'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="text.secondary">Book Export</Typography>
                                                        {activeLanguageStatus?.exportedBlobUrl ? (
                                                            <Button
                                                                size="small"
                                                                startIcon={downloadingKey === `export-${activeLanguage}` ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                                                disabled={downloadingKey === `export-${activeLanguage}`}
                                                                onClick={() => handleDownload(
                                                                    `export-${activeLanguage}`,
                                                                    activeLanguageStatus.exportedBlobUrl,
                                                                    `${bookState?.name || 'book'}-${activeLanguage}.${blobExtension(activeLanguageStatus.exportedBlobUrl, 'idml')}`
                                                                )}
                                                                sx={{ textTransform: 'none', mt: 0.5 }}
                                                            >
                                                                Download export
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">Not exported</Typography>
                                                        )}
                                                    </Grid>
                                                </Grid>

                                                {/* History table */}
                                                {loadingHistory ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3 }}>
                                                        <CircularProgress size={20} />
                                                        <Typography variant="body2" color="text.secondary">Loading history…</Typography>
                                                    </Box>
                                                ) : !hasActiveHistory ? (
                                                    <Card
                                                        variant="outlined"
                                                        sx={{ borderRadius: '8px', backgroundColor: 'rgba(46, 125, 50, 0.03)', borderStyle: 'dashed' }}
                                                    >
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                                No saved history yet
                                                            </Typography>
                                                            <Alert severity="info" sx={{ alignItems: 'center' }}>
                                                                Segment-level history is recorded once translation lands for this language, and on each subsequent edit.
                                                            </Alert>
                                                        </CardContent>
                                                    </Card>
                                                ) : (
                                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '0 0 8px 8px' }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Save Type</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Segments Changed</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Saved By</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Saved At</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {activeHistory.versions.map((version) => (
                                                                    <TableRow
                                                                        key={`${activeLanguage}-${version.versionNumber}`}
                                                                        sx={{
                                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                                                            backgroundColor: SAVE_TYPES[version.saveType]?.bgColor || 'transparent',
                                                                        }}
                                                                    >
                                                                        <TableCell>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                v{version.versionNumber}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                                {getSaveTypeIcon(version.saveType)}
                                                                                <Typography variant="caption" fontWeight={500}>
                                                                                    {SAVE_TYPES[version.saveType]?.label || version.saveType || 'Unknown'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">
                                                                                {version.changedSegments?.length ?? 0}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Tooltip title={version.createdBy || 'Unknown'}>
                                                                                <Typography variant="body2" noWrap>
                                                                                    {version.createdBy === 'system'
                                                                                        ? 'System'
                                                                                        : (version.createdBy || 'Unknown').split('@')[0]}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Tooltip title={formatTime(version.createdAt).relative}>
                                                                                <Typography variant="body2">
                                                                                    {formatTime(version.createdAt).full}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                                                {confirmingRestore && restoringVersion?.versionNumber === version.versionNumber ? (
                                                                                    <>
                                                                                        <Typography variant="caption" fontWeight={500} color="warning.main">
                                                                                            Restore v{version.versionNumber}?
                                                                                        </Typography>
                                                                                        <Button size="small" variant="contained" color="warning" onClick={confirmRestore} sx={{ minWidth: 'auto', px: 1 }}>
                                                                                            Yes
                                                                                        </Button>
                                                                                        <Button size="small" variant="outlined" color="inherit" onClick={cancelRestore} sx={{ minWidth: 'auto', px: 1 }}>
                                                                                            No
                                                                                        </Button>
                                                                                    </>
                                                                                ) : restoring && restoringVersion?.versionNumber === version.versionNumber ? (
                                                                                    <Button size="small" variant="outlined" color="secondary" disabled startIcon={<CircularProgress size={16} />} sx={{ textTransform: 'none' }}>
                                                                                        Restoring…
                                                                                    </Button>
                                                                                ) : restoreSuccess?.version === version.versionNumber ? (
                                                                                    <Button size="small" variant="contained" color="success" disableElevation startIcon={<CheckCircleIcon />} sx={{ textTransform: 'none' }}>
                                                                                        Restored
                                                                                    </Button>
                                                                                ) : (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        color="secondary"
                                                                                        startIcon={<RestoreIcon />}
                                                                                        disabled={restoring || confirmingRestore}
                                                                                        onClick={() => handleRestore(version)}
                                                                                        sx={{ textTransform: 'none' }}
                                                                                    >
                                                                                        Restore
                                                                                    </Button>
                                                                                )}
                                                                                {restoreError && restoringVersion?.versionNumber === version.versionNumber && (
                                                                                    <Alert severity="error" variant="outlined" sx={{ py: 0, px: 1 }} onClose={() => setRestoreError(null)}>
                                                                                        {restoreError}
                                                                                    </Alert>
                                                                                )}
                                                                            </Stack>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No languages available.</Typography>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BookFileDetailsDialog;
